import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sun, Moon, ChevronDown, LogOut, User, Key, Eye, EyeOff, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Logo } from '@/components/ui/Logo';
import { FlagIcon } from '@/components/ui/FlagIcon';
import { useAuth } from '@/context/AuthContext';
import { useAuthModal } from '@/context/AuthModalContext';
import { logOut, hasPasswordProvider, changePassword, setPasswordForGoogleUser, getAuthErrorMessage } from '@/services/auth';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, changeLanguage, getCurrentLanguage } from '@/i18n/config';

interface HeaderProps {
  theme: 'dark' | 'light';
  toggleTheme: () => void;
}

export function Header({ theme, toggleTheme }: HeaderProps) {
  const { t, i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const location = useLocation();
  const { user, firebaseUser, isAuthenticated } = useAuth();
  const { openAuthModal } = useAuthModal();
  const currentLang = getCurrentLanguage();
  const userHasPassword = hasPasswordProvider(firebaseUser);

  const navigation = [
    { name: t('nav.home', 'Home'), href: '/' },
    { name: t('nav.services', 'Services'), href: '/services' },
    { name: t('nav.products', 'Products'), href: '/products' },
    { name: t('nav.blog', 'Blog'), href: '/blog' },
    {
      name: t('nav.company', 'Company'),
      href: '#',
      children: [
        { name: t('nav.about', 'About'), href: '/about' },
        { name: t('nav.careers', 'Careers'), href: '/careers' },
      ],
    },
    { name: t('nav.contact', 'Contact'), href: '/contact' },
  ];

  // Close menus when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setResourcesOpen(false);
    setUserMenuOpen(false);
    setLanguageOpen(false);
  }, [location.pathname]);

  // Reset image error when user changes
  useEffect(() => {
    setImageError(false);
  }, [user]);

  const handleSignOut = async () => {
    try {
      await logOut();
      setUserMenuOpen(false);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const openPasswordModal = () => {
    setUserMenuOpen(false);
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError('');
    setPasswordSuccess(false);
    setPasswordModalOpen(true);
  };

  const closePasswordModal = () => {
    setPasswordModalOpen(false);
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError('');
    setPasswordSuccess(false);
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    // Validation
    if (passwordForm.newPassword.length < 6) {
      setPasswordError(t('auth.passwordTooShort', 'Şifre en az 6 karakter olmalıdır'));
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError(t('auth.passwordMismatch', 'Şifreler eşleşmiyor'));
      return;
    }

    setPasswordLoading(true);

    try {
      if (userHasPassword) {
        // User already has password - change it
        await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      } else {
        // Google-only user - set new password
        await setPasswordForGoogleUser(passwordForm.newPassword);
      }
      setPasswordSuccess(true);
      setTimeout(() => {
        closePasswordModal();
      }, 2000);
    } catch (error: unknown) {
      const errorCode = (error as { code?: string })?.code || '';
      setPasswordError(getAuthErrorMessage(errorCode));
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass">
      <nav className="container flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/">
          <Logo size={26} />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-1">
          {navigation.map((item) => (
            <div key={item.name} className="relative">
              {item.children ? (
                <button
                  className={cn(
                    'nav-link flex items-center gap-1',
                    resourcesOpen && 'text-accent'
                  )}
                  onClick={() => setResourcesOpen(!resourcesOpen)}
                  onBlur={() => setTimeout(() => setResourcesOpen(false), 200)}
                >
                  {item.name}
                  <ChevronDown size={16} className={cn(
                    'transition-transform',
                    resourcesOpen && 'rotate-180'
                  )} />
                </button>
              ) : (
                <Link
                  to={item.href}
                  className={cn(
                    'nav-link',
                    location.pathname === item.href && 'active'
                  )}
                >
                  {item.name}
                </Link>
              )}

              {/* Dropdown */}
              {item.children && resourcesOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 py-2 rounded-lg glass shadow-xl">
                  {item.children.map((child) => (
                    <Link
                      key={child.name}
                      to={child.href}
                      className="block px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-accent-glow transition-colors"
                      onClick={() => setResourcesOpen(false)}
                    >
                      {child.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="hidden lg:flex items-center gap-3">
          {/* Language Switcher */}
          <div className="relative">
            <button
              onClick={() => setLanguageOpen(!languageOpen)}
              className="flex items-center gap-1.5 px-2 py-2 rounded-lg hover:bg-accent-glow transition-colors"
              aria-label="Change language"
            >
              <FlagIcon code={currentLang.flagCode} size={20} />
              <ChevronDown size={14} className={cn(
                'text-text-secondary transition-transform',
                languageOpen && 'rotate-180'
              )} />
            </button>
            {languageOpen && (
              <div className="absolute right-0 top-full mt-2 w-40 py-2 rounded-lg glass shadow-xl border border-border-default">
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code);
                      setLanguageOpen(false);
                    }}
                    className={cn(
                      'w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors',
                      i18n.language === lang.code
                        ? 'text-accent bg-accent-glow'
                        : 'text-text-secondary hover:text-text-primary hover:bg-accent-glow'
                    )}
                  >
                    <FlagIcon code={lang.flagCode} size={18} />
                    <span>{lang.nativeName}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg hover:bg-accent-glow transition-colors"
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? (
              <Sun size={20} className="text-text-secondary" />
            ) : (
              <Moon size={20} className="text-text-secondary" />
            )}
          </button>

          {/* Sign In / User Menu */}
          {isAuthenticated && user ? (
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-accent-glow transition-colors"
              >
                {user.photoURL && !imageError ? (
                  <img
                    src={user.photoURL}
                    alt={user.displayName || 'User'}
                    className="w-8 h-8 rounded-full"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                    <User size={16} className="text-white" />
                  </div>
                )}
                <span className="text-sm text-text-primary hidden xl:block">
                  {user.displayName || user.email?.split('@')[0]}
                </span>
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 py-2 rounded-lg glass shadow-xl border border-border-default">
                  <div className="px-4 py-2 border-b border-border-default">
                    <p className="text-sm font-medium text-text-primary truncate">
                      {user.displayName || 'User'}
                    </p>
                    <p className="text-xs text-text-tertiary truncate">{user.email}</p>
                  </div>
                  <Link
                    to="/dashboard"
                    className="flex items-center gap-2 px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-accent-glow transition-colors"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <User size={16} />
                    {t('nav.dashboard')}
                  </Link>
                  <button
                    onClick={openPasswordModal}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-accent-glow transition-colors"
                  >
                    <Key size={16} />
                    {userHasPassword ? t('nav.changePassword', 'Şifre Değiştir') : t('nav.setPassword', 'Şifre Belirle')}
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-error hover:bg-error/10 transition-colors"
                  >
                    <LogOut size={16} />
                    {t('nav.signOut')}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={openAuthModal}
              className="btn btn-ghost text-sm"
            >
              {t('nav.signIn')}
            </button>
          )}

          {/* Contact CTA */}
          <Link to="/contact" className="btn btn-primary btn-sm">
            {t('nav.getQuote', 'Get a Quote')}
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? (
            <X size={24} className="text-text-primary" />
          ) : (
            <Menu size={24} className="text-text-primary" />
          )}
        </button>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden glass border-t border-border-default">
          <div className="container py-4 space-y-2">
            {navigation.map((item) => (
              <div key={item.name}>
                {item.children ? (
                  <>
                    <button
                      className="w-full text-left nav-link"
                      onClick={() => setResourcesOpen(!resourcesOpen)}
                    >
                      {item.name}
                    </button>
                    {resourcesOpen && (
                      <div className="pl-4 mt-2 space-y-2">
                        {item.children.map((child) => (
                          <Link
                            key={child.name}
                            to={child.href}
                            className="block nav-link text-sm"
                            onClick={() => {
                              setMobileMenuOpen(false);
                              setResourcesOpen(false);
                            }}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.href}
                    className={cn(
                      'block nav-link',
                      location.pathname === item.href && 'active'
                    )}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}

            <div className="pt-4 border-t border-border-default space-y-3">
              {/* Language Switcher for Mobile */}
              <div className="flex items-center gap-2">
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors',
                      i18n.language === lang.code
                        ? 'bg-accent text-white'
                        : 'bg-accent-glow text-text-secondary hover:text-text-primary'
                    )}
                  >
                    <FlagIcon code={lang.flagCode} size={18} />
                    <span>{lang.nativeName}</span>
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-lg hover:bg-accent-glow"
                >
                  {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                </button>
                {isAuthenticated && user ? (
                  <>
                    <Link to="/dashboard" className="btn btn-ghost btn-sm flex-1">
                      {t('nav.dashboard')}
                    </Link>
                    <button
                      onClick={handleSignOut}
                      className="btn btn-ghost btn-sm flex-1 text-error"
                    >
                      {t('nav.signOut')}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      openAuthModal();
                      setMobileMenuOpen(false);
                    }}
                    className="btn btn-ghost btn-sm flex-1"
                  >
                    {t('nav.signIn')}
                  </button>
                )}
                <Link to="/contact" className="btn btn-primary btn-sm flex-1" onClick={() => setMobileMenuOpen(false)}>
                  {t('nav.getQuote', 'Get a Quote')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Change Modal - Rendered via Portal */}
      {passwordModalOpen && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closePasswordModal}
          />
          <div className="relative w-full max-w-md rounded-2xl bg-bg-surface shadow-2xl border border-border-default">
            <div className="p-6">
              <h3 className="text-xl font-semibold mb-2 text-text-primary">
                {userHasPassword ? t('nav.changePassword', 'Şifre Değiştir') : t('nav.setPassword', 'Şifre Belirle')}
              </h3>
              <p className="text-text-secondary text-sm mb-6">
                {userHasPassword
                  ? t('auth.changePasswordDesc', 'Mevcut şifrenizi girin ve yeni şifrenizi belirleyin.')
                  : t('auth.setPasswordDesc', 'Google hesabınıza bir şifre ekleyin. Böylece e-posta ve şifre ile de giriş yapabilirsiniz.')}
              </p>

              {passwordSuccess ? (
                <div className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                  <CheckCircle2 className="text-green-500" size={24} />
                  <div>
                    <p className="font-medium text-green-400">
                      {t('auth.passwordChanged', 'Şifreniz başarıyla güncellendi!')}
                    </p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  {userHasPassword && (
                    <div>
                      <label className="block text-sm font-medium mb-2 text-text-primary">
                        {t('auth.currentPassword', 'Mevcut Şifre')}
                      </label>
                      <div className="relative">
                        <input
                          type={showCurrentPassword ? 'text' : 'password'}
                          value={passwordForm.currentPassword}
                          onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                          className="w-full px-4 py-3 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none pr-12 text-text-primary"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary"
                        >
                          {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium mb-2 text-text-primary">
                      {t('auth.newPassword', 'Yeni Şifre')}
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                        className="w-full px-4 py-3 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none pr-12 text-text-primary"
                        minLength={6}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary"
                      >
                        {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    <p className="text-xs text-text-tertiary mt-1">
                      {t('auth.passwordHint', 'En az 6 karakter')}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-text-primary">
                      {t('auth.confirmPassword', 'Şifre Tekrar')}
                    </label>
                    <input
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className="w-full px-4 py-3 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none text-text-primary"
                      minLength={6}
                      required
                    />
                  </div>

                  {passwordError && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-error/10 border border-error/30">
                      <AlertCircle className="text-error flex-shrink-0" size={18} />
                      <p className="text-sm text-error">{passwordError}</p>
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={closePasswordModal}
                      className="btn btn-ghost flex-1"
                      disabled={passwordLoading}
                    >
                      {t('common.cancel', 'İptal')}
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary flex-1"
                      disabled={passwordLoading}
                    >
                      {passwordLoading ? (
                        <Loader2 className="animate-spin" size={18} />
                      ) : (
                        t('common.save', 'Kaydet')
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
}
